/**
 * AudioContentClassifier — offline content-type heuristic over an
 * AudioBuffer. Returns a label suited for time-stretch / pitch-shift
 * engines that take a transient-handling hint (RubberBand R3, signalsmith,
 * SoundTouch).
 *
 * The classifier answers: how percussive is this audio? Output:
 *   { type: 'percussive' | 'mixed' | 'smooth',
 *     onsetDensity: number,   // onsets per second
 *     crestFactor: number,    // peak / RMS in pre-emphasised envelope
 *     confidence: number }    // 0..1
 *
 * Why we need this:
 *   RubberBand R3 has two transient-related option axes — TRANSIENTS_MODE
 *   (how detected attacks are reshaped) and DETECTOR_MODE (how attacks are
 *   detected at all). The default DETECTOR_COMPOUND is fine for most music
 *   but under-fires on dense electronic / pop kicks, smearing them on
 *   speed-up. DETECTOR_PERCUSSIVE biases toward sharp attacks. We pick the
 *   right mode automatically based on what we hear in the file.
 *
 * Algorithm (no FFT, all time-domain):
 *   1. Mix to mono (avoids stereo phase cancellation hiding kicks panned
 *      hard left/right).
 *   2. Pre-emphasis 1st-order IIR: y[n] = x[n] - 0.97 * x[n-1] — boosts
 *      the 1 kHz+ band where transient energy lives, kills DC + sub-bass
 *      drone that masks attacks.
 *   3. Half-wave rectify + 10 ms RMS frames (1 frame ≈ 480 samples @ 48 kHz).
 *   4. Adaptive peak detection: a frame is an onset if its RMS exceeds
 *      `localMean(±50 frames) * 1.5` AND `globalRMS * 0.4` (rejects quiet
 *      passages between songs / silence padding) AND `≥ 80 ms` since the
 *      previous detected onset (refractory).
 *   5. onsetDensity = onsets / duration. crestFactor = max(env) / mean(env).
 *
 * Classification thresholds:
 *   percussive:  density >= 2.0/s  AND  crest >= 4
 *   smooth:      density <= 0.5/s  AND  crest <= 2.0  AND  globalMean > silence-floor
 *   mixed:       everything else (the default safe choice)
 *
 * Both percussive AND smooth thresholds are deliberately asymmetric in
 * favour of the safer 'mixed' verdict (compound detector + crisp
 * transients = RubberBand R3 defaults):
 *
 *   - The percussive density threshold (2.0/s) is intentionally generous
 *     because the cost of a false-positive "percussive" verdict is mild
 *     (DETECTOR_PERCUSSIVE on a sustained vocal track sounds essentially
 *     the same as COMPOUND) while the cost of a false-negative — the
 *     Сергеев scenario, kick smeared on speed-up — is the precise UX bug
 *     this module exists to prevent.
 *   - The smooth thresholds (0.5/s + crest 2.0) are tight because the
 *     cost of a false-positive "smooth" verdict is high — DETECTOR_SOFT
 *     + TRANSIENTS_SMOOTH actively blurs any attacks that ARE present
 *     (the bass-heavy pop-mix-without-hats case the unit tests exposed).
 *     When in doubt, fall through to 'mixed' which preserves crisp
 *     transients without committing to either detector bias.
 *
 * These thresholds were chosen on a small fixture set (kick-only loop,
 * pop song, vocal-only, classical strings) — see pitch-battery-qa.mjs
 * cases tagged "content-classify".
 *
 * Performance: linear in sample count, ~1 ms per second of mono 48 kHz
 * audio on a midrange laptop. For long files we sample only the middle
 * 30 s — representative enough for content-type, ~1 ms total cost.
 *
 * Usage:
 *   const klass = window.AudioContentClassifier.classify(audioBuffer);
 *   // klass.type, klass.onsetDensity, klass.crestFactor, klass.confidence
 */
(function(){
'use strict';

var FRAME_MS = 10;
var REFRACTORY_MS = 80;
var THRESH_LOCAL = 1.5;     // RMS must exceed local mean × this
var THRESH_GLOBAL = 0.4;    // ...AND global mean × this (silence guard)
var LOCAL_WIN_FRAMES = 50;  // ±50 frames = ±500 ms
var SAMPLE_DURATION = 30;   // analyse only first 30 s for long files

function mixToMono(buffer, startSec, durSec) {
    var sr = buffer.sampleRate;
    var startSample = Math.max(0, Math.floor(startSec * sr));
    var endSample = Math.min(buffer.length, Math.ceil((startSec + durSec) * sr));
    var n = endSample - startSample;
    if (n <= 0) return new Float32Array(0);

    var ch = buffer.numberOfChannels;
    var out = new Float32Array(n);
    if (ch === 1) {
        out.set(buffer.getChannelData(0).subarray(startSample, endSample));
    } else {
        // Sum-to-mono with normalisation so a stereo file with hard-panned
        // kick doesn't get its energy halved relative to a mono file.
        for (var c = 0; c < ch; c++) {
            var src = buffer.getChannelData(c);
            for (var i = 0; i < n; i++) out[i] += src[startSample + i];
        }
        var inv = 1 / ch;
        for (var k = 0; k < n; k++) out[k] *= inv;
    }
    return out;
}

function preEmphasis(samples, alpha) {
    // y[n] = x[n] - α * x[n-1]. In-place is fine (forward sweep).
    var n = samples.length;
    if (n === 0) return samples;
    var prev = samples[0];
    samples[0] = 0;
    for (var i = 1; i < n; i++) {
        var cur = samples[i];
        samples[i] = cur - alpha * prev;
        prev = cur;
    }
    return samples;
}

function frameEnvelope(samples, sampleRate) {
    var frameLen = Math.max(1, Math.round(sampleRate * FRAME_MS / 1000));
    var nFrames = Math.floor(samples.length / frameLen);
    var env = new Float32Array(nFrames);
    var idx = 0;
    for (var f = 0; f < nFrames; f++) {
        var sumSq = 0;
        for (var k = 0; k < frameLen; k++) {
            var s = samples[idx++];
            sumSq += s * s;
        }
        env[f] = Math.sqrt(sumSq / frameLen);
    }
    return env;
}

function detectOnsets(env, frameMs) {
    if (env.length === 0) return [];
    var globalMean = 0;
    for (var i = 0; i < env.length; i++) globalMean += env[i];
    globalMean /= env.length;
    if (globalMean < 1e-6) return []; // silence

    var globalThresh = globalMean * THRESH_GLOBAL;
    var refractoryFrames = Math.ceil(REFRACTORY_MS / frameMs);

    var onsets = [];
    var lastOnsetFrame = -refractoryFrames;
    var win = LOCAL_WIN_FRAMES;
    for (var f = 0; f < env.length; f++) {
        if ((f - lastOnsetFrame) < refractoryFrames) continue;
        var sum = 0, count = 0;
        var lo = Math.max(0, f - win), hi = Math.min(env.length, f + win + 1);
        for (var j = lo; j < hi; j++) { sum += env[j]; count++; }
        var localMean = count > 0 ? sum / count : 0;
        var localThresh = Math.max(localMean * THRESH_LOCAL, globalThresh);
        if (env[f] > localThresh) {
            // Peak refinement — prefer the local maximum within ±2 frames
            // so density doesn't double-count a single attack across a
            // 10 ms boundary.
            var peakF = f;
            var peakV = env[f];
            for (var d = -2; d <= 2; d++) {
                var ff = f + d;
                if (ff >= 0 && ff < env.length && env[ff] > peakV) {
                    peakV = env[ff]; peakF = ff;
                }
            }
            onsets.push(peakF);
            lastOnsetFrame = peakF;
        }
    }
    return onsets;
}

function classify(buffer, opts) {
    opts = opts || {};
    if (!buffer || !buffer.numberOfChannels || !buffer.sampleRate || !buffer.length) {
        return { type: 'mixed', onsetDensity: 0, crestFactor: 0, confidence: 0 };
    }

    var dur = buffer.duration;
    var sampleDur = Math.min(dur, opts.maxSeconds || SAMPLE_DURATION);
    // For long files, sample from the middle (skip intros / fades).
    var startSec = dur > sampleDur ? Math.max(0, (dur - sampleDur) * 0.5) : 0;

    var mono = mixToMono(buffer, startSec, sampleDur);
    if (mono.length < buffer.sampleRate) {
        // less than 1 s usable — too short for a stable estimate
        return { type: 'mixed', onsetDensity: 0, crestFactor: 0, confidence: 0 };
    }

    preEmphasis(mono, 0.97);
    var env = frameEnvelope(mono, buffer.sampleRate);
    var onsets = detectOnsets(env, FRAME_MS);

    // Crest factor over the envelope tells us how "spiky" the energy
    // profile is. Sustained content (vocals, strings) has crest ~1.5-2.5;
    // percussion has crest >= 4 because attacks are 4-10× the steady RMS.
    var maxV = 0, sumV = 0;
    for (var i = 0; i < env.length; i++) {
        if (env[i] > maxV) maxV = env[i];
        sumV += env[i];
    }
    var meanV = env.length > 0 ? sumV / env.length : 0;
    var crest = meanV > 1e-9 ? maxV / meanV : 0;

    var density = sampleDur > 0 ? onsets.length / sampleDur : 0;

    // Silence guard — a buffer with essentially zero energy classifies as
    // 'mixed' rather than 'smooth'. The smooth branch otherwise catches
    // silent buffers because density=0 ≤ 0.8 and crest=0 ≤ 2.5; that's
    // technically true but misleading — silence isn't "smooth music",
    // and downstream tools should treat it as "no signal" / safe default.
    if (meanV < 1e-5) {
        return { type: 'mixed', onsetDensity: 0, crestFactor: 0, confidence: 0 };
    }

    var type, confidence;
    if (density >= 2.0 && crest >= 4) {
        type = 'percussive';
        // Stronger evidence (denser onsets, higher crest) → higher confidence.
        confidence = Math.min(1, (density - 2.0) / 5 + (crest - 4) / 8 + 0.5);
    } else if (density <= 0.5 && crest <= 2.0) {
        type = 'smooth';
        confidence = Math.min(1, (0.5 - density) / 0.5 + (2.0 - crest) / 2.0 + 0.4);
    } else {
        type = 'mixed';
        // Confidence here is low by definition — we picked the safe default.
        confidence = 0.3;
    }

    return {
        type: type,
        onsetDensity: density,
        crestFactor: crest,
        confidence: confidence
    };
}

window.AudioContentClassifier = {
    classify: classify,
    version: '1.0.0'
};

})();
